import React, { useState, useEffect, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  BeachAccess as LeaveIcon,
  Receipt as ReceiptIcon,
  Assessment as PayslipIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import PageHeader from './shared/PageHeader';
import ErrorFallback from './shared/ErrorFallback';
import StatRow from './shared/StatRow';
import StatUnit from './shared/StatUnit';
import { useSettings } from '../contexts/SettingsContext';

import PunchClockCard from './PunchClockCard';

// Lightweight error boundary to prevent PunchClockCard crashes from killing the dashboard
class PunchCardBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(error) { console.error('[PunchCardBoundary] caught:', error); return { hasError: true }; }
  componentDidCatch(error) { console.error('[PunchCardBoundary] componentDidCatch:', error); }
  render() { return this.state.hasError ? null : this.props.children; }
}

const DashboardHome = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { selectedCompanyId, isAllCompaniesView } = useCompany();
  const { isModuleVisible } = useSettings();
  const isEmployee = userProfile?.role === 'employee';
  const isManager = userProfile?.role === 'manager';
  const showPunchCard = (isEmployee || isManager) && isModuleVisible('attendance');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pendingApprovals: 0,
    headcount: 0,
    payrollStatus: 'Not run',
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isSupabaseConfigured()) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();
        const cid = (!isAllCompaniesView() && selectedCompanyId) ? selectedCompanyId : null;
        const activityLimit = 5;

        // Run all queries in parallel with individual error handling.
        // One failure (CORS, RLS, network) doesn't kill the whole dashboard.
        const safeCount = async (query) => {
          try {
            const { count, error } = await query;
            return error ? 0 : (count || 0);
          } catch { return 0; }
        };
        const safeData = async (query) => {
          try {
            const { data, error } = await query;
            return error ? [] : (data || []);
          } catch { return []; }
        };

        // Build query builders (each one isolated so failures don't cascade)
        const countQ = (table, eqFilters = {}) => {
          let q = supabase.from(table).select('*', { count: 'exact', head: true });
          if (cid) q = q.eq('company_id', cid);
          Object.entries(eqFilters).forEach(([k, v]) => { q = q.eq(k, v); });
          return q;
        };
        const dataQ = (table, select, { eqFilters = {}, orderBy = null, limit: lim = null } = {}) => {
          let q = supabase.from(table).select(select);
          if (cid) q = q.eq('company_id', cid);
          Object.entries(eqFilters).forEach(([k, v]) => { q = q.eq(k, v); });
          if (orderBy) q = q.order(orderBy.col, { ascending: orderBy.asc ?? false });
          if (lim) q = q.limit(lim);
          return q;
        };

        const [headcount, pendingLeave, pendingClaims, pendingOt, payrollData, leaveAct, claimsAct, otAct] =
          await Promise.all([
            safeCount(countQ('employees', { status: 'active' })),
            safeCount(countQ('leave_applications', { status: 'pending' })),
            safeCount(countQ('expense_claims', { status: 'pending' })),
            safeCount(countQ('overtime_requests', { status: 'pending' })),
            safeData(dataQ('payroll_records', 'status', { eqFilters: { month: currentMonth, year: currentYear } })),
            safeData(dataQ('leave_applications', 'id, employee_id, leave_type_name, status, created_at, employees(full_name)', { orderBy: { col: 'created_at', asc: false }, limit: activityLimit })),
            safeData(dataQ('expense_claims', 'id, employee_id, claim_type, description, status, created_at, employees!expense_claims_employee_id_fkey(full_name)', { orderBy: { col: 'created_at', asc: false }, limit: activityLimit })),
            safeData(dataQ('overtime_requests', 'id, employee_id, ot_type, status, created_at', { orderBy: { col: 'created_at', asc: false }, limit: activityLimit })),
          ]);

        const processedCount = payrollData.filter(r => r.status === 'processed' || r.status === 'paid').length;
        const totalPayroll = payrollData.length;
        const payrollStatus = totalPayroll === 0
          ? 'Not run'
          : processedCount === totalPayroll
          ? 'Completed'
          : `Partial (${totalPayroll - processedCount} pending)`;

        const merged = [
          ...leaveAct.map(a => ({ ...a, type: 'leave', label: a.leave_type_name || 'Leave' })),
          ...claimsAct.map(a => ({ ...a, type: 'claim', label: a.claim_type || 'Claim' })),
          ...otAct.map(a => ({ ...a, type: 'overtime', label: a.ot_type || 'Overtime' })),
        ]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, activityLimit);

        setStats({
          pendingApprovals: (pendingLeave || 0) + (pendingClaims || 0) + (pendingOt || 0),
          headcount: headcount || 0,
          payrollStatus,
        });
        setRecentActivity(merged);
      } catch (err) {
        console.error('[DashboardHome] Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedCompanyId, isAllCompaniesView]);

  if (error) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <PageHeader title="Dashboard" subtitle="Overview of your workforce" />
        <ErrorFallback title="Unable to load dashboard" message={error} />
      </Box>
    );
  }

  const kpiCards = [
    {
      label: 'Pending Approvals',
      value: stats.pendingApprovals,
      path: '/leave',
    },
    {
      label: 'Active Headcount',
      value: stats.headcount,
      path: '/employees',
    },
    {
      label: 'Payroll Status',
      value: stats.payrollStatus,
      path: '/payroll',
    },
  ];

  const adminActions = [
    { label: 'Add Employee', icon: AddIcon, path: '/employees', color: 'primary' },
    { label: 'Run Payroll', icon: CalculateIcon, path: '/payroll', color: 'primary' },
    { label: 'Approve Leave', icon: LeaveIcon, path: '/leave', color: 'primary' },
  ];

  const employeeActions = [
    { label: 'Apply Leave', icon: LeaveIcon, path: '/leave', color: 'primary' },
    { label: 'Submit Claim', icon: ReceiptIcon, path: '/expense-claims', color: 'primary' },
    { label: 'View Payslip', icon: PayslipIcon, path: '/my-payslip', color: 'primary' },
  ];

  const quickActions = isEmployee ? employeeActions : adminActions;

  const statusColor = (status) => {
    if (status === 'approved' || status === 'paid') return 'success';
    if (status === 'rejected' || status === 'cancelled') return 'error';
    return 'warning';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(date);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <PageHeader title="Dashboard" subtitle="Overview of your workforce" />

      {/* Punch Clock Card — employees/managers only when attendance module is enabled */}
      {showPunchCard && (
        <PunchCardBoundary>
          <PunchClockCard />
        </PunchCardBoundary>
      )}

      {/* KPI Cards */}
      <StatRow>
        {kpiCards.map((kpi) => (
          <Box
            key={kpi.label}
            onClick={() => kpi.path && navigate(kpi.path)}
            sx={{ cursor: kpi.path ? 'pointer' : 'default' }}
          >
            <StatUnit
              label={kpi.label}
              value={loading ? <Skeleton width={80} /> : kpi.value}
            />
          </Box>
        ))}
      </StatRow>

      {/* Quick Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {quickActions.map((action) => (
            <Button
              key={action.label}
              variant="contained"
              startIcon={<action.icon />}
              onClick={() => navigate(action.path)}
              disabled={loading}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* Recent Activity */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Recent Activity
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton variant="rounded" height={48} key={i} />
            ))}
          </Box>
        ) : recentActivity.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recent activity.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {recentActivity.map((item, index) => (
              <React.Fragment key={item.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.employees?.full_name || 'Employee'} — {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(item.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={item.status}
                      color={statusColor(item.status)}
                      size="small"
                    />
                    <Button
                      size="small"
                      endIcon={<ArrowIcon />}
                      onClick={() => {
                        if (item.type === 'leave') navigate('/leave');
                        else if (item.type === 'claim') navigate('/expense-claims');
                        else if (item.type === 'overtime') navigate('/overtime');
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </Box>
                {index < recentActivity.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardHome;
